import {useRouter} from 'next/router';
import {useCallback, useEffect} from 'react';
import {UrlLink} from '../../../applink/UrlLink';
import {requestContext} from '../../../common/requestContext';
import {useDisableWindowScroll} from '../../../common/shared/hooks/useDisableWindowScroll.hook';
import useTypedDispatch from '../../../common/useTypedDispatch';
import useTypedSelector from '../../../common/useTypedSelector';
import {SearchAutoCompleteItem} from '../../../design-system/organisms/SearchAutoCompleteItem/SearchAutoCompleteItem';
import {SearchAutoCompleteWrapper} from '../../../design-system/organisms/SearchAutoCompleteWrapper/SearchAutoCompleteWrapper';
import {SearchAutoCompleteQuery} from '../../../graphql/generated/types';
import {LOG_SEARCH_CLICK} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {addRecentKeyword} from '../../../recentSearchKeyword/recentKeywordList.storage';
import {useGetSearchKeywordQueryData} from '../hook/useGetSearchKeywordQueryData';
import {setOpenAutoComplete} from '../state/searchSlice';
import {
  selectAutoCompleteCustomProps,
  selectAutoCompleteKeywordInfoList,
} from '../state/selector';

interface SearchAutoCompleteContainerProps {
  data: SearchAutoCompleteQuery;
}

export function SearchAutoCompleteContainer({
  data,
}: SearchAutoCompleteContainerProps) {
  const searchKeyword = useTypedSelector(state => state.search.searchKeyword);
  const autoCompleteKeywordInfoList = selectAutoCompleteKeywordInfoList({
    list: data.searchAutoComplete?.list,
    searchKeyword,
  });
  const isMobile = requestContext.requestInfo.display.isMobile;

  const {searchData, searchInfiniteData} = useGetSearchKeywordQueryData();
  const autoCompleteCustomProps =
    selectAutoCompleteCustomProps(searchInfiniteData);

  const dispatch = useTypedDispatch();
  const clearAutoComplete = useCallback(() => {
    dispatch(setOpenAutoComplete(false));
  }, [dispatch]);

  const {disableWindowScroll, enableWindowScroll} = useDisableWindowScroll();
  useEffect(() => {
    if (!isMobile || !autoCompleteKeywordInfoList?.length) {
      return;
    }
    disableWindowScroll();
    return () => {
      enableWindowScroll();
    };
  }, [
    autoCompleteKeywordInfoList?.length,
    disableWindowScroll,
    enableWindowScroll,
    isMobile,
  ]);
  const router = useRouter();

  if (!isMobile && !autoCompleteKeywordInfoList?.length) {
    return null;
  }

  return (
    <SearchAutoCompleteWrapper isMobile={isMobile}>
      {autoCompleteKeywordInfoList?.map(
        ({autoCompleteKeyword, matchedSearchKeyword, url}, index) => (
          <UrlLink key={autoCompleteKeyword} url={url}>
            <SearchAutoCompleteItem
              searchKeyword={matchedSearchKeyword.matchedKeyword}
              autoCompleteKeyword={autoCompleteKeyword}
              className={isMobile ? 'h-44pxr' : 'h-34pxr'}
              isMobile={isMobile}
              onClick={e =>
                LOG_SEARCH_CLICK('자동완성검색어_클릭', e, () => {
                  addRecentKeyword(autoCompleteKeyword);
                  clearAutoComplete();
                  // 동일한 query로 된 next/link 클릭시 아무 동작이 없으므로 직접 router.replace 합니다.

                  if (autoCompleteKeyword === searchData?.keyword) {
                    router.replace({
                      pathname: router.pathname,
                      query: location.search.replace('?', ''),
                    });
                  }
                })
              }
              {...putTiaraObjectProps({
                actionKind: 'Search',
                click: {
                  layer1: '자동완성',
                  ordnum: String(index + 1),
                  copy: autoCompleteKeyword,
                },
                search: {
                  search_term: autoCompleteKeyword,
                  search_type: '자동완성검색어',
                },
                customProps: {
                  ...autoCompleteCustomProps,
                  search_input_term: searchKeyword,
                },
              })}
            />
          </UrlLink>
        )
      )}
    </SearchAutoCompleteWrapper>
  );
}
